import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Plus, UserMinus } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { Customer } from '@/lib/api/customer-api'
import { customerApi } from '@/lib/api/customer-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    customerKeys,
    useCustomerGroups,
    useCustomers,
} from '@/hooks/use-customer-query'
import { CustomerSearchSelect } from '@/components/forms/customer-search-select'
import { DiscountRulesTable } from '@/components/forms/discount-rules-table'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/customers/groups/$groupId',
)({
    component: CustomerGroupDetailPage,
})

function CustomerGroupDetailPage() {
    const { groupId } = Route.useParams()
    const qc = useQueryClient()
    const { data: groups = [] } = useCustomerGroups()
    const group = groups.find((g) => g.id === groupId)

    const { data: customersData, isLoading } = useCustomers({
        customer_group_id: groupId,
        per_page: 200,
    })
    const customers = customersData?.data ?? []

    const [addOpen, setAddOpen] = useState(false)
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
        null,
    )

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: customerKeys.lists() })
        void qc.invalidateQueries({ queryKey: customerKeys.groups() })
    }

    const assignMutation = useMutation({
        mutationFn: (customer: Customer) =>
            customerApi.update(customer.id, {
                name: customer.name,
                customer_group_id: groupId,
            }),
        onSuccess: () => {
            invalidate()
            setAddOpen(false)
            setSelectedCustomer(null)
            toast.success(m.toast_customer_update_success())
        },
        onError: () => toast.error(m.toast_customer_update_error()),
    })

    const removeMutation = useMutation({
        mutationFn: (customer: Customer) =>
            customerApi.update(customer.id, {
                name: customer.name,
            }),
        onSuccess: () => {
            invalidate()
            toast.success(m.toast_customer_update_success())
        },
        onError: () => toast.error(m.toast_customer_update_error()),
    })

    const handleAssign = () => {
        if (!selectedCustomer) return
        assignMutation.mutate(selectedCustomer)
    }

    const handleRemove = (customer: Customer) => {
        removeMutation.mutate(customer)
    }

    if (!group) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/customers/groups">
                    <Button variant="ghost" size="icon">
                        <ArrowLeft className="size-4" />
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-lg font-semibold">{group.name}</h1>
                    <p className="text-sm text-muted-foreground">
                        {group.description || m.customer_group_page_desc()}
                    </p>
                </div>
                <Badge variant="secondary">
                    {m.customer_group_discount_label()}:{' '}
                    {group.discount_percent}%
                </Badge>
            </div>

            {/* Customer list */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base">
                        {m.nav_customers()} ({customers.length})
                    </CardTitle>
                    <Button size="sm" onClick={() => setAddOpen(true)}>
                        <Plus className="size-4 mr-1" />
                        {m.customer_group_add_member()}
                    </Button>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">
                            {m.common_loading()}
                        </p>
                    ) : customers.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-8 text-center">
                            {m.customer_group_no_members()}
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        {m.customer_code_label()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_name_label()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_phone_label()}
                                    </TableHead>
                                    <TableHead className="w-20" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {customers.map((c) => (
                                    <TableRow key={c.id}>
                                        <TableCell className="font-mono text-xs">
                                            {c.code}
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                to="/customers/$customerId"
                                                params={{
                                                    customerId: c.id,
                                                }}
                                                className="text-primary hover:underline"
                                            >
                                                {c.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell>{c.phone || '—'}</TableCell>
                                        <TableCell>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                className="text-destructive"
                                                onClick={() => handleRemove(c)}
                                                disabled={
                                                    removeMutation.isPending
                                                }
                                            >
                                                <UserMinus className="size-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Discount rules */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {m.discount_rules()}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <DiscountRulesTable groupId={groupId} />
                </CardContent>
            </Card>

            {/* Add Customer Dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.customer_group_add_member()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <CustomerSearchSelect
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                        />
                        {selectedCustomer?.customer_group_id &&
                            selectedCustomer.customer_group_id !== groupId && (
                                <p className="text-xs text-amber-600">
                                    {m.customer_group_already_in_group({
                                        group:
                                            selectedCustomer.customer_group
                                                ?.name ?? '',
                                    })}
                                </p>
                            )}
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setAddOpen(false)}
                            >
                                {m.common_cancel()}
                            </Button>
                            <Button
                                onClick={handleAssign}
                                disabled={
                                    !selectedCustomer ||
                                    assignMutation.isPending
                                }
                            >
                                {m.customer_group_assign()}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
